import { useState, useEffect, useRef } from 'react';
import validator from '@rjsf/validator-ajv8';
import Form from '@rjsf/core';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';

import './device-io-settings-form.styles.scss';
import { CustomCheckboxWidget } from './custom-checkbox-widget';
import { UiSchemaGenerator } from '../../shared/ui-schema-generator';
import { findMaxTariffStructureLength } from '../../device-details-component/device-details-configuration.part';

const hiddenErrorNames = new Set(['anyOf', 'allOf', 'oneOf']);

function filterOutIrrelevantBatteryErrors(errors, form) {
  const batterySchemaMap = form.props.formContext.batterySchemaMap;
  const selectedBattery = form.state.formData?.converters?.[0]?.battery?.batteryType;
  const selectedBatteryDefinition = '#/definitions/' + batterySchemaMap[selectedBattery];

  return errors.filter((error) => {
    if (error.property?.startsWith('.converters.0.battery.')) {
      return error.schemaPath?.startsWith(selectedBatteryDefinition);
    }

    return true;
  });
}

function transformErrors(errors, form) {
  if (!form) {
    return errors;
  }

  const filterOutHiddenErrorNames = (error) => !error.name || !hiddenErrorNames.has(error.name);

  const filteredErrors = filterOutIrrelevantBatteryErrors(errors, form);

  return filteredErrors.filter(filterOutHiddenErrorNames).map((error) => {
    if (error.name === 'required') {
      error.message = 'You must provide a value for this field';
    } else if (error.name === 'maximum') {
      error.message = 'This value is too high, it must be less than or equal to ' + error.params.limit;
    } else if (error.name === 'minimum') {
      error.message = 'This value is too low, it must be greater than or equal to ' + error.message;
    } else if (error.name === 'const') {
      error.message = 'You must set this field to ' + error.params.allowedValue;
    }

    return error;
  });
}

function generateUiSchemaOrder(schemaProperties) {
  const booleanOrder = [];
  const inputOrder = [];

  Object.entries(schemaProperties).forEach(([property, value]) => {
    const { type = '' } = value;

    if (type === 'boolean') {
      booleanOrder.push(property);
    } else if (type !== 'array' && type !== 'object') {
      inputOrder.push(property);
    }
  });

  booleanOrder.push(...inputOrder, '*');

  return booleanOrder;
}

/**
 * Form component, receives a set of objects and values and a template
 * creates a form where the user can alter the values before submitting them using a provided function
 * @param {Object} props The component's properties
 */
function DeviceIOSettingsForm(props) {
  const { schema = {}, formData = {}, onSubmit: userSubmit = () => undefined, batteryTitleToReferenceMap } = props;

  const [innerData, setInnerData] = useState({});
  const [noSubmit, setNoSubmit] = useState(null);
  const [editing, setEditing] = useState(false);
  const [uiSchema, setUiSchema] = useState({});
  const formRef = useRef(null);

  useEffect(() => {
    if (!editing) {
      setInnerData(formData);
    }
  }, [editing, formData]);

  useEffect(() => {
    const { properties = {} } = schema || {};

    // The countryStructure definition moved from properties to definitions in the schema, so we need to handle both cases.
    let countryStructure = properties.countryStructure;
    if (countryStructure?.$ref) {
      countryStructure = schema?.definitions?.countryStructure;
    }
    const maxTariffLength = countryStructure ? findMaxTariffStructureLength(countryStructure) : 0;

    setUiSchema({
      'ui:order': generateUiSchemaOrder(properties),
      countryStructure: new UiSchemaGenerator.TariffStructureBuilder().setPadNestedForms(false).setMaxTariffLength(maxTariffLength).build(),
    });
  }, [schema]);

  const formProps = {
    className: 'schemaForm',
    schema,
    validator,
    uiSchema,
    formData: innerData,
    ref: formRef,
    idPrefix: 'io_root',
    disabled: !editing,
    liveValidate: true,
    onChange: (ev) => {
      const { errors = [], formData: newData } = ev;
      // ABSOLUTELY NECESSARY MAGIC - MAYBE RJSF v6 FIXES THIS
      // CONSIDER USING THIS FOR ALL ONEOF, ANYOF AND ALLOF FIELDS
      const base = document.getElementById('root_converters_0_battery__anyof_select');
      const defs = schema?.definitions || {};
      const localData = JSON.parse(JSON.stringify(newData));
      Object.entries(defs).forEach(([_k, v]) => {
        const { title } = v || {};
        const { options = [], selectedIndex } = base || {}; // Why is this a select rjsf?!!!?!!?
        let textContent = '';
        if (selectedIndex && options) {
          textContent = options[selectedIndex]?.text;
        }

        if (title && textContent && title === textContent) {
          const { properties = {} } = v || {};
          const conv = localData?.converters;
          const battery = conv[0]?.battery;
          Object.entries(properties).forEach(([k1, v1]) => {
            if (!(k1 in battery) || battery[k1] === undefined) {
              // console.log('PROBLEM', k1, v1, battery);
              const { default: df } = v1;

              battery[k1] = df;
            }
          });
        }
      });
      //const conv2 = localData?.converters;
      //const bat2 = conv2[0]?.battery;
      // END OF ABSOLUTELY NECESSARY MAGIC
      setInnerData(localData);
      setNoSubmit(errors.length > 0);
    },
    transformErrors: (e) => transformErrors(e, formRef.current),
    experimental_defaultFormStateBehavior: {
      emptyObjectFields: 'populateAllDefaults',
      allOf: 'populateDefaults',
      mergeDefaultsIntoFormData: 'useDefaultIfFormDataUndefined',
    },
    widgets: {
      CheckboxWidget: CustomCheckboxWidget,
    },
    formContext: {
      batterySchemaMap: batteryTitleToReferenceMap,
    },
  };

  const buttonProps = {
    className: 'submit-form-button',
    disabled: noSubmit,
    style: {
      float: 'right',
    },
    onClick: (_ev) => {
      const { current: { state: { formData: newData = {} } = {} } = {} } = formRef || {};

      userSubmit(newData);
      setEditing(false);
    },
  };

  const control = <Checkbox onClick={() => setEditing(!editing)} />;

  return (
    <div className="device-io-settings__form">
      <div className="device-io-settings__button-checkbox-wrapper">
        <FormControlLabel
          checked={editing}
          control={control}
          label="Override"
        />
        {editing ? <Button {...buttonProps}>SUBMIT</Button> : null}
      </div>
      <Form {...formProps}> </Form>
    </div>
  );
}

export default DeviceIOSettingsForm;
